import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { SuperAdminLayout } from './SuperAdminLayout';
import {
  Shield, Search, Filter, Activity, AlertTriangle, Check, Database, Server
} from 'lucide-react';

const ACTION_COLORS = {
  create_company: 'text-[#009b3a]',
  update_company: 'text-blue-400',
  suspend_company: 'text-red-400',
  activate_company: 'text-emerald-400',
  archive_company: 'text-gray-400',
  impersonate: 'text-amber-400',
  stop_impersonation: 'text-amber-400',
  create_user: 'text-[#009b3a]',
  update_user: 'text-blue-400',
  delete_user: 'text-red-400',
  reset_password: 'text-amber-400',
  update_subscription: 'text-[#fedd00]',
  extend_subscription: 'text-emerald-400',
  send_communication: 'text-blue-400',
  update_config: 'text-gray-400',
  toggle_maintenance: 'text-amber-400',
};

const SuperAdminLogs = () => {
  const { api } = useAuth();
  const { t, formatDate, formatNumber } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('');
  const [health, setHealth] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionFilter) params.append('action', actionFilter);
      params.append('limit', '100');
      const res = await api.get(`/super-admin/logs?${params}`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [api, actionFilter]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/system-health');
      setHealth(res.data);
    } catch (err) { console.error(err); }
  }, [api]);

  useEffect(() => { fetchLogs(); fetchHealth(); }, [fetchLogs, fetchHealth]);

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-admin-logs">
        <div>
          <h1 className="text-xl font-bold text-white">{t('saLogs.title')}</h1>
          <p className="text-sm text-gray-500">{t('saLogs.subtitle')}</p>
        </div>

        {/* System Health */}
        {health && (
          <div className="bg-[#111827] border border-white/5 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Server className="w-5 h-5 text-[#009b3a]" />
              <h2 className="text-sm font-semibold text-white">{t('saLogs.systemHealth')}</h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="flex items-center gap-2">
                <Database className={`w-4 h-4 ${health.database === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`} />
                <span className="text-xs text-gray-300">{t('saLogs.database')}: </span>
                <span className={`text-xs font-bold ${health.database === 'healthy' ? 'text-emerald-400' : 'text-red-400'}`}>
                  {health.database === 'healthy' ? t('saLogs.statusOk') : t('saLogs.statusError')}
                </span>
              </div>
              {Object.entries(health.collections || {}).map(([name, count]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">{name}:</span>
                  <span className="text-xs font-bold text-white">{formatNumber(count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter */}
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            data-testid="filter-log-action"
            className="bg-[#111827] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-gray-300 focus:outline-none"
          >
            <option value="">{t('saLogs.filterAll')}</option>
            <option value="create_company">{t('saLogs.filterCreateCompany')}</option>
            <option value="update_company">{t('saLogs.filterUpdateCompany')}</option>
            <option value="suspend_company">{t('saLogs.filterSuspend')}</option>
            <option value="activate_company">{t('saLogs.filterActivate')}</option>
            <option value="impersonate">{t('saLogs.filterImpersonate')}</option>
            <option value="create_user">{t('saLogs.filterCreateUser')}</option>
            <option value="delete_user">{t('saLogs.filterDeleteUser')}</option>
            <option value="reset_password">{t('saLogs.filterResetPassword')}</option>
            <option value="update_subscription">{t('saLogs.filterUpdateSubscription')}</option>
            <option value="update_config">{t('saLogs.filterUpdateConfig')}</option>
          </select>
          <span className="text-xs text-gray-500">{t('saLogs.entriesCount', { count: formatNumber(total) })}</span>
        </div>

        {/* Logs */}
        <div className="bg-[#111827] border border-white/5 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <Shield className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500">{t('saLogs.empty')}</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {logs.map(log => (
                <div key={log.id} className="px-4 py-3 flex items-start gap-3 hover:bg-white/[0.02]" data-testid={`log-${log.id}`}>
                  <Activity className={`w-4 h-4 flex-shrink-0 mt-0.5 ${ACTION_COLORS[log.action] || 'text-gray-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-semibold ${ACTION_COLORS[log.action] || 'text-gray-400'}`}>
                        {log.action}
                      </span>
                      <span className="text-[10px] text-gray-600">{t('saLogs.onTarget', { target: log.target_type })}</span>
                    </div>
                    {log.details && <p className="text-xs text-gray-400 mt-0.5 truncate">{log.details}</p>}
                    <p className="text-[10px] text-gray-600 mt-0.5">
                      {t('saLogs.byUserAt', { user: log.admin_username, date: formatDate(log.created_at) })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminLogs;
